import { RequestDetailResponse } from '@/api/apps/insider';
import { useTranslate } from '@/hooks/useTranslate';
import { Box, Chip, Divider, Typography } from '@mui/material';

interface Props {
    data: RequestDetailResponse['evaluation'] | undefined
}

const ProjectEvaluation = ({ data }: Props) => {
    const { t } = useTranslate('insider');

    if (!data) return null;

    const solution_data = data.solution_data;
    const technological_stack = data.technological_stack || [];
    const team_members = data.team_members || [];
    const development_steps = data.development_steps || [];
    const extra_costs = data.extra_costs || [];
    const approx = data.approx;

    const renderFromTo = (postfix: string, items: number[] | undefined) => {
        if (!items) return 'N/A';
        items = items.filter((item, index) => index < 2);
        if (items.length === 0) return 'N/A';
        if (items.length === 1) return `${items[0]} ${postfix}`;
        return `${items[0]} - ${items[1]} ${postfix}`;
    }


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        }}>
            {/* Solution Data */}
            {solution_data && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        {t('evaluation.solution-data')}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}>
                        <Typography variant="h4">
                            {solution_data.description}
                        </Typography>
                        <Typography variant="body2">
                            <b>{t('evaluation.type')}:</b> {solution_data.type || t('evaluation.not-specified')}
                        </Typography>
                        <Typography variant="subtitle1">
                            <b>{t('evaluation.programming-languages')}:</b>{' '}
                            {solution_data.programming_languages?.join(', ') || t('evaluation.not-specified')}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Divider />

            {/* Technological Stack */}
            {technological_stack?.length > 0 && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        Technological Stack
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gap: 3,
                    }}>
                        {technological_stack.map((stack, index) => (
                            <Box key={index}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {stack.name?.join(', ') || 'Unknown'} ({stack.side})
                                </Typography>
                                <Typography variant="body2">{stack.description || 'No description available.'}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            <Divider />

            {/* Team Members */}
            {team_members?.length > 0 && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        Team Members
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gap: 3,
                    }}>
                        {team_members.map((member, index) => (
                            <Box key={index}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                    {member.role} (x{member.amount})
                                </Typography>
                                <Typography variant="body2">
                                    <b>Note:</b> {member.note || 'No additional notes.'}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Hourly Rate:</b> ${member.hourly_rate_usd?.toFixed(2) || 'N/A'}/hr
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            <Divider />

            {/* Development Steps */}
            {development_steps?.length > 0 && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        Development Steps
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                    }}>
                        {development_steps?.map((step, index) => (
                            <Box key={index} sx={{
                                display: 'flex',
                                gap: 5,
                            }}>
                                <Chip label={index + 1} color="primary" />
                                <Box>

                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {step.step} ({step.complexity})
                                    </Typography>

                                    <Typography variant="body2" gutterBottom>
                                        {step.description}
                                    </Typography>

                                    <Typography variant="subtitle2" color="textSecondary">
                                        <b>Hours Needed:</b> {step.hours_needed || 'N/A'}
                                    </Typography>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <b>Difficulties:</b> {step.difficulties?.join(', ') || 'None'}
                                    </Typography>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <b>Assigned Team Members:</b> {step.team_member_assigned?.join(', ') || 'None'}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            <Divider />

            {/* Extra Costs */}
            {extra_costs?.length > 0 && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        Extra Costs
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                    }}>
                        {extra_costs.map((cost, index) => (
                            <Box key={index}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {cost.description}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <b>Cost:</b> ${cost.cost_usd?.[0]} - ${cost.cost_usd?.[1]} ({cost.period})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            <Divider />

            {/* Approximation */}
            {approx && (
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        Approximation
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}>
                        <Typography variant="body1" color="success">
                            <b>Development Time:</b> {renderFromTo('months', approx.development_months)}
                        </Typography>
                        <Typography variant="body1" color="success">
                            <b>Total Cost:</b> {renderFromTo('USD', approx.total_cost_usd)}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ProjectEvaluation;
